/**
 * Service for retrieving user/creator info and searching user gifs.
 *
 * Responsibilities:
 * - Fetch user/creator profile by username/id.
 * - Fetch creator tags.
 * - Search gifs for a specific user with filters and paging.
 */
import type {
  ICreatorTagsResponse,
  IUser,
  IGifList,
} from "../types";

import type { ApiClient } from "@/common/api/http/types";

export default class UsersService {
  public constructor(private readonly client: ApiClient) {}

  async getUser(params: { userName: string }): Promise<IUser> {
    return await this.client.get(`v1/users/${params.userName}`);
  }

  async fetchCreatorById(creatorId: string): Promise<IUser> {
    return await this.client.get(`v1/users/${creatorId}`);
  }

  async fetchCreatorTagsById(creatorId: string): Promise<ICreatorTagsResponse> {
    return await this.client.get(`v2/creators/${creatorId}/tags`);
  }

  async searchUserGifs(params: {
    username: string;
    order: string;
    count: number;
    type?: string;
    page?: number;
    tags?: string;
    verified?: boolean;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (params.order) {
      query.append("order", params.order);
    }

    if (params.count) {
      query.append("count", params.count.toString());
    }

    if (params.type) {
      query.append("type", params.type);
    }

    if (params.page) {
      query.append("page", params.page.toString());
    }

    if (params.tags) {
      query.append("tags", params.tags);
    }

    const queryString = query.toString();
    return await this.client.get(`v2/users/${params.username}/search?${queryString}`);
  }
}
